import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { Span } from "./Span";
import AutoSizer from "react-virtualized-auto-sizer";

export type TableCell = ReactElement | string;

export type TableHeader<TColumnKey extends string> = {
  key: TColumnKey;
  value: TableCell;
  sortable?: boolean;
};

export type TableRow = {
  data: TableCell[];
};

export type TableProps<TColumnKey extends string> = {
  header?: TableHeader<TColumnKey>[];
  rows?: TableRow[];
  scrollWidth?: number;
  onSortChange?: (key: TColumnKey, colIndex: number, direction: "DESC" | "ASC") => void;
};

const cellClassName = "whitespace-nowrap px-2 py-2 text-center";
const rowClassName = "border-b border-neutral-110";

type WidthAndHeight = { height: number; width: number };

type _TableProps<TColumnKey extends string> = WidthAndHeight & TableProps<TColumnKey>;

const _Table = <TColumnKey extends string,>({
  height,
  width,
  header = [],
  rows = [],
  scrollWidth = 200,
  onSortChange,
}: _TableProps<TColumnKey>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(Infinity);

  const [sortIndex, setSortIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"DESC" | "ASC">("DESC"); // intentionally DESC, first click will make it ASC

  const onClickLeftArrow = useCallback(() => {
    if (!tableContainerRef.current) return;
    tableContainerRef.current.scrollLeft -= scrollWidth;
    setScrollPosition(tableContainerRef.current.scrollLeft);
  }, [scrollWidth]);

  const onClickRightArrow = useCallback(() => {
    if (!tableContainerRef.current) return;
    tableContainerRef.current.scrollLeft += scrollWidth;
    setScrollPosition(tableContainerRef.current.scrollLeft);
  }, [scrollWidth]);

  const onScroll = useCallback(() => {
    if (!tableContainerRef.current) return;
    setScrollPosition(tableContainerRef.current.scrollLeft);
  }, []);

  const showCaretLeft = scrollPosition !== 0;
  const showCaretRight = scrollPosition < maxScroll;

  useEffect(() => {
    if (!tableRef.current) return;
    if (!tableContainerRef.current) return;
    const { width: tableContainerWidth } = tableContainerRef.current.getBoundingClientRect();
    const { width: tableWidth } = tableRef.current.getBoundingClientRect();
    setMaxScroll(Math.floor(tableWidth - tableContainerWidth));
  }, []);

  return (
    <div style={{ height, width, display: "flex", flexDirection: "row" }}>
      <div ref={tableContainerRef} className="overflow-x-auto scroll-smooth" onScroll={onScroll}>
        <table ref={tableRef}>
          <thead className={rowClassName}>
            <tr>
              {header.map((cell, i) => {
                const sortable = cell.sortable === true || cell.sortable === undefined;

                return (
                  <th
                    className={cellClassName}
                    key={cell.key}
                    onClick={
                      sortable && onSortChange !== undefined
                        ? () => {
                            setSortIndex(i);
                            const newDirection = sortDirection === "ASC" ? "DESC" : "ASC";
                            setSortDirection(newDirection);
                            onSortChange(cell.key, i, newDirection);
                          }
                        : undefined
                    }
                  >
                    {cell.value}
                    {i === sortIndex && sortable ? (sortDirection === "DESC" ? "▲" : "▼") : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ data }, i) => (
              <tr key={i} className={rowClassName}>
                {data.map((cell, j) => (
                  <td key={j} className={cellClassName}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ width: 70, display: "flex", flexDirection: "row", padding: "5px" }}>
        <Span neutral={!showCaretLeft} size="sm">
          <Icon name="caret-left" onClick={showCaretLeft ? onClickLeftArrow : undefined} />
        </Span>
        <Span neutral={!showCaretRight} size="sm">
          <Icon name="caret-right" onClick={showCaretRight ? onClickRightArrow : undefined} />
        </Span>
      </div>
    </div>
  );
};

export const Table = <TColumnKey extends string,>(props: TableProps<TColumnKey>) => {
  // Table must be inside inner-component for its useEffect to properly execute with
  // provided height & width
  return (
    <AutoSizer disableHeight>{({ height, width }) => <_Table {...props} height={height} width={width} />}</AutoSizer>
  );
};
